import React, {useMemo, useImperativeHandle, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, useSlotRecipe, Text} from '@chakra-ui/react3'

import {hasValidAsset, hasValidImageSrc, isVideoAsset} from '../../../utils/utils'
import Video from '../video'
import VideoControl from '../video-control'
import Image from '../image'
import useVideoControl from '../../../hooks/useVideoControl'
import {assetSlotRecipe} from './index.recipe'

/**
 * Asset component to display image or video
 *
 * @component
 * @param {object} [asset] - Optional asset object
 * @param {object|function} [videoRef] - Ref object or callback for the video element
 * @returns {JSX.Element|null} Rendered asset or null if no asset provided
 */

const Asset = ({asset, videoRef, loading, widths, objectFit, variant = 'default'}) => {
    const ref = useRef(null)
    const [isPlaying, onVideoToggle, controls] = useVideoControl(asset?.autoplay, ref)

    // Keep `isPlaying` in sync with the actual video state since `onVideoToggle` flips state.
    // This preserves the existing imperative `play()`/`pause()` consumer API without changes.
    useImperativeHandle(videoRef, () => ({
        play: controls.play,
        pause: controls.pause
    }))

    const recipe = useSlotRecipe({recipe: assetSlotRecipe})
    const styles = useMemo(() => recipe({variant}), [recipe, variant])

    if (!hasValidAsset(asset)) return null

    if (isVideoAsset(asset)) {
        const {videoName, duration} = asset

        return (
            <Box {...styles.container}>
                <Video
                    ref={ref}
                    {...asset}
                    loading={loading}
                    widths={widths}
                    objectFit={objectFit}
                />
                <Box {...styles.bottomGradient}></Box>
                <Box {...styles.videoControlBox}>
                    <VideoControl
                        isPlaying={isPlaying}
                        onToggle={onVideoToggle}
                        variant={asset?.showVideoInfo ? 'mediaFilled' : 'mediaGhost'}
                        {...((variant === 'gallery' || variant === 'teaser') && {size: 'md'})}
                    />
                    {videoName && (
                        <Text textStyle="body.small" color="text.on-media.primary">
                            {videoName}
                        </Text>
                    )}
                    {duration && (
                        <Text textStyle="body.small" color="text.on-media.primary">
                            {duration}
                        </Text>
                    )}
                </Box>
            </Box>
        )
    }

    if (hasValidImageSrc(asset)) {
        return (
            <Box {...styles.container}>
                <Image
                    {...asset}
                    loading={loading}
                    objectFit={objectFit}
                    fetchpriority={loading === 'eager' ? 'high' : undefined}
                    widths={widths}
                    // fetchpriority="high" optimizes LCP (Largest Contentful Paint) by prioritizing
                    // hero image download over other resources. Combined with loading="eager" and
                    // preload, this ensures fastest possible above-the-fold render.
                />
            </Box>
        )
    }
}

Asset.propTypes = {
    asset: PropTypes.object,
    videoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    loading: PropTypes.string,
    widths: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    objectFit: PropTypes.string,
    variant: PropTypes.string
}

export default Asset
